import { useCallback } from 'react';

import { useCRMEventsContext } from 'state/crm-events';
import { useLocale } from 'state/intl';
import { changeUser, enableBrazeSdk, setEmail, wipeBrazeData } from 'utils/braze';
import { brand, env } from 'utils/environment';
import { login as ldLogin, logout as ldLogout } from 'utils/launchdarkly';

import { UserDetails } from './types';

export const useThirdPartyAuthentication = () => {
  const { region } = useLocale();
  const { loginCRM, logoutCRM } = useCRMEventsContext();

  const logUserInToThirdPartyServices = useCallback(
    async (currentUser: UserDetails) => {
      // Await ldLogin to ensure LaunchDarkly flags are updated before the user can interact with the UI.
      // This fixes an issue where flags targeted by email (like gift cards) weren't showing
      // on fresh install until the app was restarted.
      await ldLogin(currentUser);

      loginCRM({
        customerId: currentUser.cognitoId,
        rbiCognitoId: currentUser.cognitoId,
        ...currentUser.details,
        brand: brand().toUpperCase(),
        region,
        env: env() as string,
      });

      changeUser(currentUser.cognitoId);

      // Update Braze Email for the user
      setEmail(currentUser.details.email);
    },
    [loginCRM, region]
  );

  const logUserOutOfThirdPartyServices = useCallback(() => {
    ldLogout();
    logoutCRM();

    // The Braze documentation does not state this, but when calling Braze.wipeData()
    // The Braze SDK will be disabled permanently, at least on iOs.
    // Therefore, we need to call Braze.enableSDK()to re-enable the SDK.
    wipeBrazeData();
    enableBrazeSdk();
  }, [logoutCRM]);

  return { logUserInToThirdPartyServices, logUserOutOfThirdPartyServices };
};
